import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table, Button, Tag, Space, Input, Select, Card, Typography,
  Drawer, Form, Row, Col, Badge, Tooltip, message, Popconfirm,
} from "antd";
import {
  PlusOutlined, SearchOutlined, ThunderboltOutlined,
  ArrowRightOutlined, InfoCircleOutlined,
} from "@ant-design/icons";
import { solicitudesApi } from "../api/client";
import type { SolicitudArea, EstadoSolicitudEnum, PrioridadEnum, NivelEnum } from "../types";

const { Title, Text } = Typography;
const { Option } = Select;

// ── Estado color map ──────────────────────────────────────────────────────────
const ESTADO_COLORS: Record<EstadoSolicitudEnum, string> = {
  "Nueva": "blue",
  "En revisión": "orange",
  "Aprobada→Proyecto": "green",
  "En desarrollo IABP": "purple",
  "Escalada": "red",
  "Descartada": "default",
  "En espera": "warning",
};

const PRIORIDAD_COLORS: Record<string, string> = {
  Urgente: "red", Alta: "orange", Media: "blue", Baja: "default",
};

const NIVEL_OPTIONS = ["Baja", "Media", "Alta"];
const TIPO_OPTIONS = ["IA/Agente", "Automatización", "Análisis de datos", "Mejora de proceso", "Mixto"];
const PRIORIDAD_OPTIONS = ["Urgente", "Alta", "Media", "Baja"];
const ESTADO_OPTIONS: EstadoSolicitudEnum[] = [
  "Nueva", "En revisión", "Aprobada→Proyecto", "En desarrollo IABP", "Escalada", "Descartada", "En espera",
];

// ── Create Form ───────────────────────────────────────────────────────────────
function CrearSolicitudDrawer({
  open, onClose, onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await solicitudesApi.crear(values);
      message.success("Solicitud registrada correctamente");
      form.resetFields();
      onSuccess();
      onClose();
    } catch (err: any) {
      if (err?.response?.data?.detail) {
        message.error(err.response.data.detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title={
        <Space>
          <ThunderboltOutlined style={{ color: "#1a1f5e" }} />
          <span>Nueva solicitud de área</span>
        </Space>
      }
      width={560}
      open={open}
      onClose={onClose}
      footer={
        <Space style={{ float: "right" }}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="primary" loading={loading} onClick={handleSubmit} style={{ background: "#1a1f5e" }}>
            Registrar solicitud
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="area_solicitante" label="Área solicitante" rules={[{ required: true }]}>
              <Input placeholder="Ej: Operaciones Madrid" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="solicitante_nombre" label="Nombre del solicitante" rules={[{ required: true }]}>
              <Input placeholder="Nombre y apellidos" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="descripcion_pain_point" label="Descripción del problema / necesidad" rules={[{ required: true, min: 10 }]}>
          <Input.TextArea rows={4} placeholder="Describe el problema o la oportunidad de mejora..." showCount maxLength={2000} />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="tipo_solicitud" label="Tipo de solicitud" rules={[{ required: true }]}>
              <Select placeholder="Selecciona tipo">
                {TIPO_OPTIONS.map((t) => <Option key={t} value={t}>{t}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="prioridad" label="Prioridad" rules={[{ required: true }]}>
              <Select placeholder="Prioridad">
                {PRIORIDAD_OPTIONS.map((p) => <Option key={p} value={p}>{p}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="complejidad" label="Complejidad" rules={[{ required: true }]}>
              <Select>{NIVEL_OPTIONS.map((n) => <Option key={n} value={n}>{n}</Option>)}</Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="esfuerzo_estimado_iabp" label="Esfuerzo" rules={[{ required: true }]}>
              <Select>{NIVEL_OPTIONS.map((n) => <Option key={n} value={n}>{n}</Option>)}</Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="impacto_esperado" label="Impacto" rules={[{ required: true }]}>
              <Select>{NIVEL_OPTIONS.map((n) => <Option key={n} value={n}>{n}</Option>)}</Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="siguiente_paso" label="Siguiente paso / comentario">
          <Input.TextArea rows={2} placeholder="Acción inmediata prevista..." />
        </Form.Item>
      </Form>
    </Drawer>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function SolicitudesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<SolicitudArea[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState<EstadoSolicitudEnum | undefined>();
  const [page, setPage] = useState(1);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await solicitudesApi.listar({
        page, page_size: 20,
        estado: estadoFiltro,
      });
      setData(res.data.items);
      setTotal(res.data.total);
    } catch {
      message.error("Error cargando solicitudes");
    } finally {
      setLoading(false);
    }
  }, [page, estadoFiltro]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleCrearProyecto = async (id: string) => {
    try {
      const res = await solicitudesApi.crearProyecto(id);
      message.success(`Proyecto ${res.data.proj_id} creado`);
      navigate(`/proyectos/${res.data.id}`);
    } catch (err: any) {
      message.error(err.response?.data?.detail ?? "Error creando proyecto");
    }
  };

  const filtered = data.filter((s) =>
    !search ||
    s.sol_id.toLowerCase().includes(search.toLowerCase()) ||
    s.area_solicitante.toLowerCase().includes(search.toLowerCase()) ||
    s.descripcion_pain_point.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    {
      title: "ID",
      dataIndex: "sol_id",
      width: 90,
      render: (v: string) => <Text code style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: "Área",
      dataIndex: "area_solicitante",
      width: 160,
      ellipsis: true,
    },
    {
      title: "Descripción",
      dataIndex: "descripcion_pain_point",
      ellipsis: true,
      render: (v: string) => <Text style={{ fontSize: 13 }}>{v}</Text>,
    },
    {
      title: "Tipo",
      dataIndex: "tipo_solicitud",
      width: 140,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: "Prioridad",
      dataIndex: "prioridad",
      width: 90,
      render: (v: string) => <Tag color={PRIORIDAD_COLORS[v] ?? "default"}>{v}</Tag>,
    },
    {
      title: "Estado",
      dataIndex: "estado",
      width: 170,
      render: (v: EstadoSolicitudEnum) => <Tag color={ESTADO_COLORS[v]}>{v}</Tag>,
    },
    {
      title: "",
      width: 110,
      render: (_: unknown, record: SolicitudArea) => (
        <Space size={4}>
          {record.sugerir_quick_win && (
            <Tooltip title="Candidata a Quick Win">
              <ThunderboltOutlined style={{ color: "#faad14" }} />
            </Tooltip>
          )}
          <Button
            type="link" size="small" icon={<ArrowRightOutlined />}
            onClick={() => navigate(`/solicitudes/${record.id}`)}
          >
            Ver
          </Button>
          {record.puede_crear_proyecto && (
            <Popconfirm
              title="¿Crear proyecto desde esta solicitud?"
              onConfirm={() => handleCrearProyecto(record.id)}
              okText="Crear"
              cancelText="No"
            >
              <Button type="link" size="small" style={{ color: "#52c41a" }}>
                → Proyecto
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <Space style={{ justifyContent: "space-between", width: "100%" }}>
          <div>
            <Title level={4} style={{ margin: 0, color: "#1a1f5e" }}>Solicitudes de Áreas</Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Primer filtro de demanda interna · Total: {total}
            </Text>
          </div>
          <Button
            type="primary" icon={<PlusOutlined />}
            onClick={() => setDrawerOpen(true)}
            style={{ background: "#1a1f5e" }}
          >
            Nueva solicitud
          </Button>
        </Space>
      </div>

      {/* Filtros */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={12} align="middle">
          <Col flex={1}>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Buscar por ID, área o descripción..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col>
            <Select
              style={{ width: 180 }}
              placeholder="Todos los estados"
              allowClear
              value={estadoFiltro}
              onChange={(v) => { setEstadoFiltro(v); setPage(1); }}
            >
              {ESTADO_OPTIONS.map((e) => <Option key={e} value={e}>{e}</Option>)}
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Tabla */}
      <Card size="small">
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{
            current: page,
            pageSize: 20,
            total,
            onChange: setPage,
            showSizeChanger: false,
            showTotal: (t) => `${t} solicitudes`,
          }}
          onRow={(record) => ({
            style: { cursor: "pointer" },
            onClick: () => navigate(`/solicitudes/${record.id}`),
          })}
        />
      </Card>

      <CrearSolicitudDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSuccess={cargar}
      />
    </div>
  );
}
